<script lang="ts" setup>
import { Tag } from "vant";
import { computed, onActivated, ref } from "vue";
import UserAvatar from "@/components/UserAvatar.vue";
import {
  useDefaultAvatar,
  useMateTypeTextByMate,
  usePreviewImg,
  useRawImageUrl,
  useVisibilityText,
} from "@/use/common";
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import { useRouter } from "vue-router";
import { isImage } from "@/modules/common";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import ReactionMate from "@/components/mobile/reply/ReactionMate.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import BaseImg from "./common/BaseImg.vue";
import escapeHtml from "@/modules/escape-html";

import { GAL_GAME_URL_PREFIX } from "@/stores/storyGameEditor";
import { useReply } from "@/stores/reply";
import { useGlobalModal } from "@/stores/globalModal";
import { timeAgo } from "@/modules/common";
import { Media, Moment, Reply } from "@/types/entity";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useMate } from "@/stores/mate";
import { MomentMedia } from "@/types/moment";
import { removeHtmlTag } from "@/tools/text";
import { MemoryType } from "@/enums/mateEnums";

const mateStore = useMate();
// Props & Emit
interface ThreadItemProps {
  moment: Moment;
  showComment?: boolean;
  enableReactionBtn?: boolean;
  replyTo?: Reply;
  enableTextCollapsed?: boolean;
  enableAudioPlay?: boolean;
  enableShowMoreLink?: boolean; // 串文：hasNext为true时，显示查看更多按钮
  showAvatar?: boolean; // 是否显示头像
  matchedText?: string; // 需要高亮的文本
  showCoinVote?: boolean;
  enableWish?: boolean;
  showInteractionArea?: boolean;
  showReplyList?: boolean;
  showCreateTime?: boolean;
  showVisibility?: boolean;
}

const props: ThreadItemProps = withDefaults(defineProps<ThreadItemProps>(), {
  showComment: true,
  enableReactionBtn: true,
  enableTextCollapsed: true,
  enableAudioPlay: false,
  enableShowMoreLink: false,
  showAvatar: true,
  showCoinVote: false,
  showInteractionArea: true,
  showReplyList: false,
  showCreateTime: true,
  showVisibility: true,
});

const hasNext = !!props.moment.childId;
const hasPrev = !!props.moment.parentId;

const emit = defineEmits<{
  (e: "threadClick", uuid: string): void;
  (e: "commentClick"): void;
  (e: "reactionClick", moment: Moment): void;
}>();
const messageList = computed(() => {
  if (!props.moment.chatMessagezz || props.moment.chatMessagezz.length === 0) {
    return [];
  }
  return props.moment.chatMessagezz.map((item) => {
    return {
      id: item.id,
      currentUser: item.mate.uuid === props.moment.mate.uuid,
      avatarUrl: item.mate.mediaUrl,
      msg: item.text,
      name: item.mate.name,
    };
  });
});
const router = useRouter();
// const user = useGlobalUser();
const replyStore = useReply();
const escapedContent = computed(() => {
  if (!props.moment.text) {
    return "";
  }
  return escapeHtml(props.moment.text);
});
const displayContent = computed(() => {
  if (!props.moment.text) {
    return "";
  }
  // 1. 先按照换行符分割，保留7行
  const splitByLine = escapedContent.value.split("\n").slice(0, 7);
  // 2. 再按照字符数分割，保留前100个字符
  return splitByLine.join("\n").slice(0, 128);
});

const needCollapsed =
  props.enableTextCollapsed! &&
  !!props.moment.text &&
  props.moment.text!.length !== displayContent.value.length;

const textCollapsed = ref<boolean>(needCollapsed);

const contentHtml = computed(() => {
  if (!props.moment.text) {
    return "";
  }

  const content = escapedContent.value.replace(/\n/g, "<br/>");
  if (props.matchedText) {
    const reg = new RegExp(props.matchedText, "g");
    return content.replace(reg, (matched) => {
      return `<span class="bg-yellow-500 text-neutral">${matched}</span>`;
    });
  }
  return content;
});

const isSingleLongImgMedia = computed(() => {
  return (
    props.moment.mediazz &&
    props.moment.mediazz.length === 1 &&
    (props.moment.mediazz[0].type === 1 ||
      isImage(props.moment.mediazz[0].url)) &&
    props.moment.mediazz[0].height! > props.moment.mediazz[0].width!
  );
});

const mediaCols = computed(() => {
  if (isSingleLongImgMedia.value) {
    return 2;
  }
  const num = props.moment.mediazz?.length ?? 0;
  if (num <= 1) return 1;
  else if (num <= 4) return 2;
  else return 3;
});

// const coinVoteInfo = computed(() => {
//   let type = CoinBalanceRecordEntityType.Mate;
//   let item: Mate | StoryBook | GalGame = props.moment.mate;
//   if (props.moment.galGame) {
//     type = CoinBalanceRecordEntityType.Game;
//     item = props.moment.galGame;
//   } else if (props.moment.story) {
//     type = CoinBalanceRecordEntityType.StoryBook;
//     item = props.moment.story.storyBook!;
//   }
//   return { type, item };
// });

// const donateInfo = computed(() => {
//   let type = "";
//   let item: any = {};
//   if (props.moment.galGame) {
//     type = "Game";
//     item = props.moment.galGame;
//   } else if (props.moment.story) {
//     type = "StoryBook";
//     item = props.moment.story;
//   } else {
//     type = "Moment";
//     item = props.moment;
//   }
//   return { type, item };
// });

// Event Handler
const chatShareClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({
    name: "shareChatPreview",
    params: { uuid: props.moment.uuid },
  });
};
const onImageClick = (
  e: UIEvent,
  mediaList: Media[] | MomentMedia[],
  idx: number
) => {
  e.stopPropagation();
  useGlobalModal().openMediaPreviewModal(
    mediaList.map((item) => useRawImageUrl(item.url)),
    idx
  );
};

const routeToMateHome = (e: UIEvent, mateUuid: string) => {
  e.stopPropagation();
  toMateDetail(mateUuid);
};
const onStoryClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({
    name: "readStory",
    params: { uuid: props.moment!.story.uuid },
  });
};

const clickMomentAvatar = (e: UIEvent, mateUuid: string) => {
  toMateDetail(mateUuid);
};

const toMateDetail = async (uuid: string) => {
  router.push({
    name: "mateHome",
    params: { uuid: uuid },
  });
};

const clickOpenGame = (uuid: string, nodeId: number) => {
  window.open(
    GAL_GAME_URL_PREFIX + `?uuid=${uuid}&nodeId=${nodeId}&type=story`
  );
};
const onThreadClick = () => {
  if (props.moment.childId) {
    router.push({
      name: "momentChain",
      query: {
        rootId: props.moment.id,
        mateId: props.moment.mate!.id,
      },
    });
  } else {
    router.push({
      name: "threadDetail",
      params: { uuid: props.moment.uuid },
    });
  }
};

const clickCreateSceneMemory = () => {
  sessionStorageSet("memory:edit:source", {
    momentzz: [props.moment],
  });
  router.push({
    name: "mateMemoryEdit",
    query: {
      type: MemoryType.Scene,
      mateId: props.moment.mate.id,
      hasSource: 1,
    },
  });
};

onActivated(() => {
  textCollapsed.value = needCollapsed;
});
</script>
<template>
  <div
    :class="{
      'pb-0 border-b-0': hasNext && !enableShowMoreLink,
      'pt-0': hasPrev,
      'border-b': !hasNext || enableShowMoreLink,
    }"
    class="flex flex-col w-full border-b py-2 cursor-pointer"
    @click="onThreadClick"
  >
    <div class="flex gap-2">
      <!-- Avatar -->
      <div
        v-if="showAvatar"
        :class="{ 'has-next': hasNext, 'has-prev': hasPrev }"
        class="flex flex-col items-center"
      >
        <user-avatar
          :mate="moment.mate"
          class="h-10 shrink-0 xl:h-11 cursor-pointer max-w-[40px]"
          @click.stop="(e) => clickMomentAvatar(e, moment.mate.uuid)"
        />
      </div>
      <!--  标题 & 类型 & 时间  -->
      <div
        :class="{ 'show-more-placeholder': hasPrev }"
        class="flex-grow flex flex-col overflow-hidden"
      >
        <div
          class="flex flex-col flex-grow gap-1"
          @click="emit('threadClick', moment.uuid!)"
        >
          <div class="flex-grow">
            <div class="flex justify-between gap-1">
              <div class="flex flex-col">
                <div class="flex flex-col">
                  <div
                    class="text-base-content font-bold"
                    @click.stop="(e) => routeToMateHome(e, moment.mate.uuid)"
                  >
                    {{ moment.mate.name ?? "" }}
                  </div>
                </div>
                <div
                  v-if="moment.mate.type"
                  class="text-xs text-base-content/50"
                >
                  {{ useMateTypeTextByMate(moment.mate) }}
                </div>
                <div v-if="replyTo" class="text-xs">
                  {{ $t("Reply.reply") }}
                  <span
                    class="text-base-content/50"
                    @click="(e) => routeToMateHome(e, replyTo!.mate.uuid)"
                  >
                    @{{ replyTo.mate.name }}
                  </span>
                </div>
              </div>
              <div class="gap-2 flex text-xs">
                <div
                  v-if="
                    moment.visibility !== 1 &&
                    moment.visibility !== 5 &&
                    showVisibility
                  "
                  class="badge badge-sm border-base-content/20"
                >
                  {{ useVisibilityText(moment.visibility) }}
                </div>
                <div
                  v-if="showCreateTime && moment.dtCreate"
                  class="text-base-content/50 whitespace-nowrap"
                >
                  {{ timeAgo(moment.dtCreate) }}
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col relative mt-2">
            <div class="flex flex-col gap-3">
              <!-- 文本内容 -->
              <div
                v-if="moment.text && !matchedText"
                class="text-base-content whitespace-pre-line"
              >
                <html-ellipsis
                  v-if="props.enableTextCollapsed"
                  :collapse-text="$t('Buttons.collapse')"
                  :content="moment.text"
                  :expand-text="$t('Buttons.viewAll')"
                  :rows="5"
                />
                <div v-else class="prose prose-sm" v-html="moment.text"></div>
                <div
                  v-if="moment.mentionMatezz && moment.mentionMatezz.length > 0"
                  class="mt-2 flex gap-2 flex-wrap"
                >
                  <Tag
                    v-for="mentionMate in moment.mentionMatezz"
                    :key="mentionMate.uuid"
                    class="badge badge-neutral"
                    @click.stop="toMateDetail(mentionMate.uuid)"
                  >
                    @{{ mentionMate.name }}
                  </Tag>
                </div>
              </div>
              <div
                v-else-if="moment.text && matchedText"
                class="text-base-content whitespace-pre-line"
                v-html="contentHtml"
              ></div>

              <!--  图片内容 -->
              <div
                v-if="moment.mediazz && moment.mediazz.length > 0"
                class="grid gap-1 media-container"
              >
                <template v-for="(media, idx) in moment.mediazz">
                  <div
                    v-if="media.mateMedia?.isUnlockRequired !== true"
                    :key="idx"
                    class="rounded overflow-hidden max-h-[400px] sm:max-h-[600px] lg:max-h-[700px] relative"
                    @click.stop="(e) => onImageClick(e, moment.mediazz!, idx)"
                  >
                    <div
                      v-if="
                        media.width &&
                        media.height &&
                        media.height / media.width > 1.5
                      "
                      class="absolute left-1 top-1 z-10 bg-black/50 backdrop-blur p-1 rounded text-white text-xs"
                    >
                      {{ $t("Message.longImage") }}
                    </div>
                    <BaseImg
                      :key="media.url"
                      v-if="
                        media.type === 1 ||
                        isImage(media.url) ||
                        media.mateMedia?.isUnlockRequired
                      "
                      :mate="moment.mate"
                      :mate-media="media.mateMedia"
                      :media="media"
                      :url="media.videoCoverUrl ?? media.url"
                      :width="
                        moment.mediazz.length >= 3
                          ? 300
                          : moment.mediazz.length === 2
                          ? 400
                          : media.width
                      "
                      class="w-full h-full rounded overflow-hidden bg-base-300"
                      fit="cover"
                      high-quality
                    />
                    <video
                      v-else
                      :poster="media.videoCoverUrl ?? ''"
                      :src="media.url"
                      class="max-w-full"
                      controls
                      crossorigin="anonymous"
                    />
                  </div>
                </template>
              </div>

              <!-- 故事内容 -->
              <div v-if="moment?.story" class="flex flex-col gap-2">
                <div v-if="moment.storyGame">
                  <div
                    class="relative h-[180px] box-content"
                    @click.stop="
                      clickOpenGame(
                        moment.storyGame.uuid!,
                        moment.story.storyGameStoryNodeId!
                      )
                    "
                  >
                    <div
                      class="absolute top-0 left-0 right-0 bottom-0 w-full rounded overflow-hidden"
                    >
                      <VanImage
                        :src="
                          moment.story.media
                            ? usePreviewImg(moment.story.media.url)
                            : useDefaultAvatar()
                        "
                        alt=""
                        class="w-full h-full rounded blur-sm"
                        fit="cover"
                        crossorigin="anonymous"
                      />
                    </div>
                    <div
                      class="absolute top-0 left-1/2 -translate-x-1/2 w-[180px] h-[180px] rounded max-w-[180px] max-h-[180px]"
                    >
                      <BaseImg :media="moment.story.media" fit="cover" />
                    </div>

                    <!-- <div
                    
                  >
                    <BaseImg :media="moment.story.media" fit="cover" />
                  </div> -->

                    <button
                      class="btn btn-sm btn-primary absolute bottom-2.5 right-2.5"
                    >
                      {{ $t(`Buttons.play`) }}
                    </button>
                  </div>
                </div>
                <div
                  v-if="moment.mediazz?.[0]?.type === 2 || moment.storyGame"
                  @click.stop="onStoryClick"
                >
                  <div
                    class="flex flex-col flex-grow gap-1 h-full overflow-hidden"
                  >
                    <div class="text-base">
                      {{ moment?.story.title }}
                    </div>
                    <div class="text-base-content/50 text-sm line-clamp-3">
                      {{ moment?.story.description }}
                    </div>
                  </div>
                </div>
                <story-list-item
                  v-else
                  :story="moment.story"
                  class="border px-2 rounded bg-base-200"
                  @click.stop="onStoryClick"
                />

                <div
                  v-if="moment.story?.storyBook"
                  class="flex gap-1 items-center"
                >
                  <div class="text-xs whitespace-nowrap">
                    {{ $t("story.collectedOn") }}
                  </div>
                  <div
                    class="badge text-xs gap-1 cursor-pointer flex"
                    @click.stop="
                      $event.stopPropagation();
                      router.push({
                        name: 'storyBookHome',
                        params: {
                          uuid: moment.story?.storyBook?.uuid,
                        },
                      });
                    "
                  >
                    <book-icon class="icon-xs" />
                    <span class="flex-1 line-clamp-1">
                      {{ moment.story?.storyBook?.title }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="moment.collectionzz?.length > 0"
                class="flex gap-1 items-center flex-wrap"
              >
                <div class="text-xs whitespace-nowrap">
                  {{ $t("story.collectedOn") }}
                </div>
                <div
                  v-for="collection in moment.collectionzz"
                  :key="collection.id"
                  class="badge text-xs gap-1 cursor-pointer flex"
                  @click.stop="
                    $event.stopPropagation();
                    router.push({
                      name: 'collectionMain',
                      params: {
                        uuid: collection.uuid,
                      },
                    });
                  "
                >
                  <i class="iconfont icon-collection text-sm"></i>
                  <span class="flex-1 line-clamp-1">
                    {{ collection.title }}
                  </span>
                </div>
              </div>
              <!-- 游戏内容 -->
              <CardGalGameHome
                v-if="moment?.galGame"
                :key="moment.galGame.uuid"
                :game="moment.galGame"
                :show-author="false"
                class="border"
                show-desc
              />

              <!-- 合集内容 -->
              <CollectionItem
                v-if="moment.collection"
                :key="moment.collection.id"
                :collection="moment.collection"
                class="border p-2 rounded"
              />

              <!-- 问答内容 -->
              <div
                v-if="moment.roleplayAnswer"
                class="card bg-base-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div class="card-body p-4">
                  <h3 class="text-base font-bold">
                    {{ moment.roleplayAnswer.question!.text }}
                  </h3>
                  <p class="text-xs text-base-content/70">
                    {{ moment.roleplayAnswer.question!.explanation }}
                  </p>
                  <div>
                    <div class="flex flex-col gap-2">
                      <div class="rounded-lg flex items-center gap-1">
                        <div class="w-10">
                          <UserAvatar
                            :mate="moment.roleplayAnswer.mate"
                            @click.stop="
                              (e) => routeToMateHome(e, moment.roleplayAnswer.mate!.uuid)
                            "
                          />
                        </div>
                        <p class="text-xs text-base-content/70 flex-1">
                          {{ moment.roleplayAnswer.mate!.name }}：
                        </p>
                      </div>
                    </div>
                    <!-- <p class=""> -->
                    <p
                      class="prose prose-sm"
                      v-html="moment.roleplayAnswer.text"
                    ></p>
                    <!-- <VanTextEllipsis
                    rows="3"
                    class="text-base-content/90 mt-1"
                    :content="moment.roleplayAnswer.text"
                    :expand-text="$t(`Buttons.expand`)"
                    :collapse-text="$t(`Buttons.collapse`)"
                    @click-action.stop
                  /> -->
                  </div>
                </div>
              </div>
              <!-- 聊天内容 -->
              <div
                v-if="messageList && messageList.length > 0"
                class="flex-col max-h-64 overflow-hidden border p-2 rounded relative thread-chat-share-item bg-base-200"
                @click.stop="chatShareClick"
              >
                <chat-message-item
                  v-for="item in messageList.slice(0, 4)"
                  :key="item.id"
                  :avatar-url="item.avatarUrl"
                  :msg="item.msg"
                  :name="item.name"
                  :position="item.currentUser ? 'end' : 'start'"
                  class="flex-grow"
                />
              </div>
            </div>

            <div
              v-if="moment.isAiGenerated && moment.mediazz?.length > 0"
              class="flex items-center justify-end gap-2 pt-1"
            >
              <span
                class="text-base-content/50 text-xs flex items-center tooltip tooltip-top"
                :data-tip="$t('Message.filterAiContentTip')"
              >
                <i class="iconify mingcute--question-line"></i>
                {{ $t("Message.contentHasAiContent") }}
              </span>
            </div>

            <!-- 回复内容 -->
            <div
              v-if="moment.fromMate"
              class="flex items-center text-xs text-base-content/50 gap-1"
            >
              <span>{{ $t("Moment.collaborativeCreation") }}</span>
              <span
                class="badge badge-primary badge-sm"
                @click="(e) => routeToMateHome(e, moment.fromMate!.uuid)"
              >
                @{{ moment.fromMate.name }}
              </span>
            </div>

            <!-- 交互 -->
            <div v-if="showInteractionArea" class="flex flex-col gap-2 mt-2">
              <reaction-mate :reply="moment.reply" />
              <div class="flex w-full justify-between items-end">
                <div class="flex gap-2 flex-wrap">
                  <button
                    v-if="moment.type !== 2"
                    class="btn btn-xs font-normal rounded"
                    @click.stop="replyStore.popEmoji(moment.reply)"
                  >
                    <i class="iconify mingcute--thumb-up-line" />
                  </button>
                  <button
                    v-if="showComment"
                    class="btn btn-xs font-normal rounded"
                    @click="emit('commentClick')"
                  >
                    <i class="iconify mingcute--comment-line" />
                    <span v-if="moment.reply && moment.reply.replyCount > 0">
                      {{ moment.reply?.replyCount }}
                    </span>
                  </button>

                  <button
                    v-if="
                      mateStore.isMyMateById(moment.mate.id) &&
                      moment.type !== 2
                    "
                    class="btn btn-xs btn-outline"
                    @click.stop="clickCreateSceneMemory"
                  >
                    +记忆
                  </button>

                  <!-- <div  class="btn btn-xs p-0"> -->
                  <audio-play
                    v-if="moment.audioMedia"
                    :mate-tone-id="moment.mate.mateToneId!"
                    :msg="moment.text"
                    :url="moment.audioMedia.url"
                  />
                  <!-- </div> -->

                  <!-- <Donate
                    v-if="showCoinVote"
                    :item="donateInfo.item"
                    :type="donateInfo.type"
                    teleport="#main-view"
                  /> -->
                  <!-- <coin-vote
                    v-if="showCoinVote"
                    :item="coinVoteInfo.item"
                    :type="coinVoteInfo.type"
                    teleport="#main-view"
                  /> -->
                  <reaction-counter :reply="moment.reply" />
                </div>
                <div class="flex gap-1 items-center">
                  <div
                    class="btn btn-xs btn-ghost btn-square"
                    @click.stop="emit('reactionClick', moment)"
                  >
                    <i class="iconify mingcute--more-1-line" />
                  </div>
                </div>
              </div>
            </div>
            <!-- <div
              v-if="false"
              class="absolute top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] rounded-[4px] flex items-center justify-center text-white text-xs backdrop-filter backdrop-blur-sm -mt-2"
            >
              <div
                class="flex flex-col gap-2.5 items-center px-2 text-center font-semibold"
              >
                <i class="iconfont icon-eye-slash text-[30px]"></i>

                <h3 class="text-base mt-1">
                  {{ $t(`purchase.premium.title`) }}
                </h3>
                <p class="text-sm">{{ $t(`purchase.premium.content`) }}</p>

                <button
                  class="btn btn-private btn-sm flex items-center mt-5"
                  @click.stop="clickPrivateContent"
                >
                  <i class="iconfont icon-eye-regular"></i>
                  <span>{{ $t(`purchase.premium.view`) }}</span>
                </button>
              </div>
            </div> -->
          </div>
        </div>
      </div>
    </div>

    <!-- 查看更多 -->
    <div v-if="hasNext && enableShowMoreLink" class="text-base-content/50">
      <div class="flex gap-2 items-center">
        <div class="flex flex-col items-center has-prev">
          <user-avatar
            :avatar-url="moment.mate.mediaUrl"
            class="h-10 shrink-0"
            @click="(e) => routeToMateHome(e, moment.mate.uuid)"
          />
        </div>
        <div
          class="text-primary show-more-placeholder"
          @click.stop="
            $event.stopPropagation();
            router.push({
              name: 'momentChain',
              query: {
                rootId: props.moment!.id,
                mateId: props.moment!.mate!.id,
              },
            });
          "
        >
          {{ $t("Moment.viewAllContent") }}
        </div>
      </div>
    </div>
    <div
      class="mt-2 ml-9 bg-base-200 p-2 rounded-md flex flex-col gap-1 text-xs"
      v-if="showReplyList && moment.reply?.descendantzz?.length"
    >
      <div v-for="reply in moment.reply?.descendantzz" :key="reply.id">
        <div class="flex items-center gap-2">
          <div class="line-clamp-3">
            <span
              class="text-primary font-bold"
              @click.stop="toMateDetail(reply.mate.uuid)"
            >
              {{ reply.mate.name }}
            </span>
            <span class="text-base-content/80">
              :
              <template v-if="reply.parentId !== reply.rootId && reply.parent">
                <span>
                  回复
                  <b>@{{ reply.parent.mate.name }}</b>
                </span>
              </template>
              {{ removeHtmlTag(reply.content) }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="moment.reply?.replyCount > 3"
        class="text-base-content/50 text-xs"
      >
        <div class="text-base-content/50 text-xs">
          {{ $t("Buttons.viewAll") }}
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="postcss" scoped>
.has-next::after {
  content: "";
  display: block;
  width: 0;
  height: 100%;
  @apply border-l-secondary/40 border-l-2;
}

.has-prev::before {
  content: "";
  display: block;
  width: 0;
  height: 10px;
  @apply border-l-secondary/40 border-l-2 shrink-0;
}

.show-more-placeholder::before {
  content: "";
  display: block;
  width: 0;
  height: 10px;
}

.media-container {
  --grid-cols: v-bind(mediaCols);
  grid-template-columns: repeat(var(--grid-cols, 1), 1fr);
  display: grid;
}

:deep(.tooltip::before) {
  font-size: 8px;
  text-align: left;
  white-space: normal;
  word-wrap: break-word;
  width: 100%;
  padding: 0 5px;
}
</style>
